/* @jsx h */
import { h, ref } from "vue";
import { TableStatus } from "@lemmonhq/types";
import { getPrintJobForTableBill } from "@app/vue/components/CheckoutComponent/CheckoutActions";
import { ShopContextType, useShop } from "@app/vue/providers/index";
import { ToastContextType, useToast } from "@app/vue/providers/Toast";
import { TablePayment } from "./payments/TablePayment";
import { ChangeTableModal } from "./ChangeTableModal";
import { TransferItemsToTableModal } from "./TransferItemsToTableModal";

export const printTableBill = async (
  tableStatus: TableStatus,
  shop: ShopContextType,
  toast: ToastContextType,
) => {
  const subtotal = tableStatus.total_amount;
  const printJob = await getPrintJobForTableBill(
    tableStatus.id,
    undefined,
    subtotal,
    tableStatus.table,
  );
  await shop.printReceipt(printJob);
  toast.showToast("Bill printed successfully", {
    type: "success",
    icon: "print",
    autoHide: 3000,
  });
};

export type UseTableDetailsActionsOptions = {
  getTableStatus: () => TableStatus | undefined;
  onTableUpdated?: () => void;
};

export const useTableDetailsActions = ({
  getTableStatus,
  onTableUpdated,
}: UseTableDetailsActionsOptions) => {
  const showPaymentModal = ref(false);
  const showChangeTableModal = ref(false);
  const showTransferItemsModal = ref(false);
  const shop = useShop();
  const toast = useToast();

  const notifyTableUpdated = () => {
    onTableUpdated?.();
  };

  const showPayment = () => {
    showPaymentModal.value = true;
  };

  const printReceipt = async () => {
    const tableStatus = getTableStatus();
    if (!tableStatus) return;

    await printTableBill(tableStatus, shop, toast);
  };

  const renderModals = () => {
    const tableStatus = getTableStatus();
    if (!tableStatus) return null;

    return (
      <>
        {showPaymentModal.value && (
          <TablePayment
            tableStatus={tableStatus}
            onClose={() => {
              showPaymentModal.value = false;
              notifyTableUpdated();
            }}
            onSuccess={() => {
              notifyTableUpdated();
            }}
          />
        )}
        {showChangeTableModal.value && (
          <ChangeTableModal
            currentTable={tableStatus}
            onClose={() => (showChangeTableModal.value = false)}
            onSuccess={() => {
              showChangeTableModal.value = false;
              toast.showToast("Table changed successfully", {
                type: "success",
                autoHide: 3000,
                icon: "table_restaurant",
              });
              notifyTableUpdated();
            }}
          />
        )}
        {showTransferItemsModal.value && (
          <TransferItemsToTableModal
            currentTable={tableStatus}
            onClose={() => (showTransferItemsModal.value = false)}
            onSuccess={() => {
              showTransferItemsModal.value = false;
              notifyTableUpdated();
            }}
          />
        )}
      </>
    );
  };

  return {
    showPaymentModal,
    showChangeTableModal,
    showTransferItemsModal,
    showPayment,
    printReceipt,
    renderModals,
  };
};
